import { Action, ActionReducer } from '@ngrx/store';
import { localStorageSync, LocalStorageConfig, defaultMergeReducer } from 'ngrx-store-localstorage';
import { SESSION_RESET } from '../state/session/session.actions';

// A type no reducer handles, used only to coax initialState out of a
// createReducer()-built reducer.
const SEED_INITIAL_STATE = '@apgap/seed-initial-state';

/**
 * Creates a localStorage sync meta-reducer with consistent defaults.
 *
 * Two behaviours of ngrx-store-localstorage are corrected here:
 *
 * 1. The wrapper does `nextState = { ...state }` for every action except
 *    @ngrx/store/init, and spreading undefined yields {}. Because feature
 *    reducers registered via provideState first run under
 *    @ngrx/store/update-reducers (the root store is created before any
 *    feature registers), slices booted with {} instead of initialState and
 *    every non-persisted field stayed undefined for the session.
 * 2. localStorage is read once, at construction, and that same snapshot is
 *    re-merged on every init/update-reducers action. After a session reset the
 *    snapshot belongs to the previous account, so it is invalidated rather than
 *    left to resurrect stale slices when a lazy feature registers.
 *
 * @param keys - Array of state keys to sync to localStorage.
 *               Can be strings or objects for nested key selection.
 * @param options - Optional additional configuration options.
 * @returns A meta-reducer that syncs specified keys to localStorage.
 *
 * @example
 * // Simple usage with string keys
 * const metaReducers = [createLocalStorageSyncReducer(['user', 'settings'])];
 *
 * @example
 * // With nested key selection
 * const metaReducers = [createLocalStorageSyncReducer([
 *   { navigationState: ['sideNavMenuItems'] },
 *   { organizationState: ['organizationList'] }
 * ])];
 */
export function createLocalStorageSyncReducer<T>(
  keys: (string | object)[],
  options: Partial<LocalStorageConfig> = {}
): (reducer: ActionReducer<T>) => ActionReducer<T> {
  return (reducer: ActionReducer<T>): ActionReducer<T> => {
    let rehydrationInvalidated = false;

    const synced = localStorageSync({
      keys,
      rehydrate: true,
      removeOnUndefined: true,
      mergeReducer: (state: T, rehydratedState: unknown, action: Action) =>
        rehydrationInvalidated ? state : defaultMergeReducer(state, rehydratedState, action),
      ...options,
    })(reducer);

    return (state: T | undefined, action: Action): T => {
      if (action.type === SESSION_RESET) {
        rehydrationInvalidated = true;
      }

      const seeded = state === undefined ? reducer(undefined, { type: SEED_INITIAL_STATE }) : state;

      return synced(seeded, action);
    };
  };
}
